import type { MeetingModelJson } from "@performyard/service-contracts";

import {
	useCompanyCalendarIntegrationSettings,
	useUserCalendarIntegrationStatus,
} from "../../../../api/hooks/defs/calendar-integration";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

type UseCalendarIntegrationParams = {
	meeting: MeetingModelJson;
};

type UseCalendarIntegrationReturn = {
	isCalendarIntegrationEnabled: boolean;
	hasCalendarEvent: boolean;
	isCalendarMismatch: boolean;
	calendarProvider: string | undefined;
};

/**
 * Hook for managing calendar integration state and checks.
 * Encapsulates feature flag, company settings, and user status checks.
 * @param meeting - The current meeting object
 */
export const useCalendarIntegration = ({ meeting }: UseCalendarIntegrationParams): UseCalendarIntegrationReturn => {
	const { value: ffIsCalendarIntegrationEnabled } = useFeatureFlagQuery("ff-is-calendar-integration-enabled", false);

	const { data: companyCalendarIntegrationSettings } = useCompanyCalendarIntegrationSettings({
		enabled: ffIsCalendarIntegrationEnabled,
	});

	const { data: userCalendarStatus } = useUserCalendarIntegrationStatus({
		enabled: ffIsCalendarIntegrationEnabled && companyCalendarIntegrationSettings?.enabled === true,
	});

	const isCalendarIntegrationEnabled =
		ffIsCalendarIntegrationEnabled &&
		companyCalendarIntegrationSettings?.enabled === true &&
		userCalendarStatus?.isConnected === true &&
		["google", "outlook"].includes(userCalendarStatus?.provider || "") &&
		Boolean(userCalendarStatus?.selectedCalendarId);

	const hasCalendarEvent = Boolean(meeting.calendarEvent?.eventId);
	const calendarProvider = meeting.calendarEvent?.provider;

	const isCalendarMismatch =
		meeting.calendarEvent?.eventId && meeting.calendarEvent?.provider && userCalendarStatus
			? !(
					userCalendarStatus.provider === meeting.calendarEvent.provider &&
					userCalendarStatus.selectedCalendarId === meeting.calendarEvent.calendarId
				)
			: false;

	return {
		isCalendarIntegrationEnabled,
		hasCalendarEvent,
		isCalendarMismatch,
		calendarProvider,
	};
};
